import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "365",
  "slug": "why-is-the-ocean-different-colors-from-space",
  "title": "Why Is the Ocean Different Colors? Reading Sea Color From Space",
  "excerpt": "From deep navy to turquoise to muddy brown, the sea is never just 'blue.' Here's what ocean colour reveals about depth, currents, and life, and how to read it from satellite imagery.",
  "date": "2026-06-07",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1491772135384-20292e4b5ea1?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHx0dXJxdW9pc2UlMjBvY2VhbiUyMGNvYXN0fGVufDF8MHx8fDE3ODA4NDU0MjN8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "The ocean's colour comes from how water absorbs and scatters light plus what's floating in it: sediment, microscopic plankton, and the depth of the water. Those colours are readable geographic clues from satellite imagery.",
  "content": [
    {
      "type": "paragraph",
      "text": "Stare at satellite imagery of the world's coastlines and you quickly notice that the ocean is almost never a single colour. It shades from near-black in the deep open sea to brilliant turquoise over tropical reefs, turns chalky brown where great rivers meet the coast, and sometimes blooms an eerie milky green for hundreds of kilometres. None of that is random. The colour of the sea is a readable signal, and learning to interpret it tells you a surprising amount about geography."
    },
    {
      "type": "heading",
      "text": "Why the Sea Looks Blue"
    },
    {
      "type": "paragraph",
      "text": "Pure water is not actually colourless. It very slightly absorbs the red end of sunlight while letting blue light penetrate and scatter back out. In a glass the effect is too faint to notice, but stack up hundreds of metres of clear water and the blue becomes deep and obvious. That is why the open ocean, far from land and full of clean deep water, appears as the darkest, richest blue of all. The more water there is and the cleaner it is, the deeper the blue."
    },
    {
      "type": "heading",
      "text": "Shallow Water and the Turquoise Tropics"
    },
    {
      "type": "paragraph",
      "text": "In shallow seas, sunlight reaches the bottom and bounces back up before the water can absorb much of it. Over a pale sand or coral seabed, that reflected light turns the water the bright turquoise and aquamarine that defines tropical lagoons, the Bahamas, and coral atolls. From above, those luminous bands of colour are a near-instant giveaway: brilliant turquoise almost always means shallow water over a light bottom, often a reef."
    },
    {
      "type": "heading",
      "text": "Brown Water: Rivers Carrying the Land to Sea"
    },
    {
      "type": "paragraph",
      "text": "Where big rivers empty into the ocean they carry enormous loads of suspended silt and mud scoured from far inland. That sediment reflects light strongly and stains the water brown or tan, sometimes in a plume that fans out for tens of kilometres offshore before it finally settles and clears. The muddy outflow of the Amazon, the Ganges, and the Yellow River, which is literally named for the colour of its sediment, are all visible from space as great smudges of brown bleeding into the blue."
    },
    {
      "type": "heading",
      "text": "Green Water: The Ocean Coming Alive"
    },
    {
      "type": "paragraph",
      "text": "A greenish tint usually means life. Microscopic floating plants called phytoplankton contain chlorophyll, the same pigment that makes leaves green, and when conditions are right they multiply into blooms dense enough to colour the sea from orbit. These blooms tend to concentrate where nutrients are abundant, near coasts, at river mouths, and in regions where deep water wells up to the surface. Tracking ocean colour is actually one of the main ways scientists monitor the health and productivity of the seas."
    },
    {
      "type": "heading",
      "text": "Reading Currents and Upwelling"
    },
    {
      "type": "paragraph",
      "text": "Because colour follows sediment, temperature, and plankton, it also traces the movement of the ocean itself. Swirls and eddies become visible where waters of different colour fold into one another. Cold, nutrient-rich water rising along certain coastlines, a process called upwelling, often shows up as a ribbon of greener, more productive sea hugging the shore. What looks like an abstract painting from space is in fact a map of how the ocean is circulating."
    },
    {
      "type": "heading",
      "text": "When the Sea Turns Red or White"
    },
    {
      "type": "paragraph",
      "text": "Occasionally the ocean takes on colours that have nothing to do with depth or sediment at all. Certain algal blooms tint coastal water a rusty red, the phenomenon long known as a red tide, which can turn miles of shoreline an unsettling brown-red. Others, made of tiny chalk-plated organisms called coccolithophores, scatter light so strongly that they turn enormous patches of sea a milky, almost glowing turquoise-white, bright enough to be unmistakable from orbit. These dramatic colour events come and go within weeks, but they are a vivid reminder that the sea's colour is alive and constantly shifting rather than fixed."
    },
    {
      "type": "heading",
      "text": "Using Ocean Color to Place Yourself"
    },
    {
      "type": "paragraph",
      "text": "All of this makes the sea a quiet source of geographic clues. Brilliant turquoise hints at a tropical reef coastline. A brown plume points to a major river mouth nearby. A cold green coastal sea suggests an upwelling zone on a continent's western edge. Even without any land in frame, the colour and texture of the water can narrow down where on Earth you might be looking."
    },
    {
      "type": "paragraph",
      "text": "Before identifying a place from water colour, check nearby landforms and the image context. Sediment, depth, and lighting can produce similar colours in different parts of the ocean."
    }
  ]
};
